import React, { useState, useEffect } from "react";
import { 
  Box, 
  Table, 
  Badge, 
  Text, 
  HStack, 
  Button, 
  Flex,
  Heading,
  Spinner,
  VStack
} from "@chakra-ui/react";
import { Check, X, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import OrderService from "./OrderService";

const getStatusPalette = (status) => {
  switch (status) {
    case "COMPLETED": return "green";
    case "PROCESSING": return "blue";
    case "CANCELLED": return "red";
    case "ERROR": return "red";
    case "REJECTED": return "red";
    case "PARTIAL_ERROR": return "orange";
    case "SUSPENDED": return "orange";
    case "PENDING_APPROVAL": return "yellow";
    default: return "gray";
  }
};

const BooleanIcon = ({ value }) => {
  return value ? (
    <Check size={18} color="green" />
  ) : (
    <X size={18} color="red" />
  );
};

export default function DataListView() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [expandedErrorId, setExpandedErrorId] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const json = await OrderService.getOrders(currentPage);
        
        setData(json);
        setIsLoading(false);

      } catch (err) {
        console.error("Order fetching error:", err);
        setError("Could not load orders. Please check your network connection.");
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [currentPage]);


  if (isLoading) {
    return (
      <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="sm" minH="400px">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">Data Processing Jobs</Heading>
        </Flex>
        <Flex direction="column" align="center" justify="center" h="300px" gap={4}>
          <Spinner size="xl" color="blue.600" />
          <Text color="gray.500">Loading your orders...</Text>
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="sm">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">Data Processing Jobs</Heading>
        </Flex>
        <VStack align="center" justify="center" p={10} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200" gap={3}>
          <AlertCircle size={32} color="#E53E3E" />
          <Text color="red.700" fontWeight="medium" textAlign="center">{error}</Text>
          <Button mt={2} onClick={() => setCurrentPage(0)} variant="outline" colorPalette="red">
            Try Again
          </Button>
        </VStack>
      </Box>
    );
  }

  const { content = [], totalPages = 1, totalElements = 0 } = data || {};

  return (
    <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="sm">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.800">Data Processing Jobs</Heading>
        <Text color="gray.500" fontSize="sm">
          Total Elements: {totalElements}
        </Text>
      </Flex>

      <Box overflowX="auto">
        <Table.Root variant="line" size="md">
          <Table.Header>
            <Table.Row bg="gray.50">
              <Table.ColumnHeader>ID</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Owner</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">LTA</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Transformation</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Online Data</Table.ColumnHeader>
              <Table.ColumnHeader>Created At</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {content.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7} textAlign="center" py={8}>
                  <Text color="gray.500">No orders found.</Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              content.map((item) => (
                <React.Fragment key={item.id}>
                <Table.Row _hover={{ bg: "gray.50" }}>
                  <Table.Cell>
                    <Text fontFamily="mono" fontSize="sm" title={item.id}>
                      {item.id}
                    </Text>
                  </Table.Cell>

                  <Table.Cell>
                    <VStack align="start" gap={1}>
                      <Badge colorPalette={getStatusPalette(item.status)} variant="subtle">
                        {item.status}
                      </Badge>
                      {item.errorMessage && (
                        <HStack
                          gap={1}
                          color="red.600"
                          fontSize="xs"
                          cursor="pointer"
                          onClick={() => setExpandedErrorId(expandedErrorId === item.id ? null : item.id)}
                          title={item.errorMessage}
                        >
                          <AlertCircle size={12} />
                          <Text textDecoration="underline">
                            {item.errorJobCount > 1 ? `${item.errorJobCount} failed jobs` : "View error"}
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </Table.Cell>

                  <Table.Cell>{item.owner}</Table.Cell>

                  <Table.Cell textAlign="center">
                    <Flex justify="center"><BooleanIcon value={item.lta} /></Flex>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Flex justify="center"><BooleanIcon value={item.transformation} /></Flex>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Flex justify="center"><BooleanIcon value={item.onlineData} /></Flex>
                  </Table.Cell>

                  <Table.Cell>
                    <Text fontSize="sm">
                      {new Date(item.createdAt).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Text>
                  </Table.Cell>
                </Table.Row>
                {expandedErrorId === item.id && item.errorMessage && (
                  <Table.Row bg="red.50">
                    <Table.Cell colSpan={7}>
                      <VStack align="start" gap={2} p={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="red.800">
                          Error details
                        </Text>
                        <Text fontSize="sm" color="red.900">{item.errorMessage}</Text>
                        {item.errorDetails && item.errorDetails !== item.errorMessage && (
                          <Box
                            as="pre"
                            fontSize="xs"
                            bg="red.100"
                            p={3}
                            borderRadius="md"
                            maxH="240px"
                            overflowY="auto"
                            whiteSpace="pre-wrap"
                            w="full"
                          >
                            {item.errorDetails}
                          </Box>
                        )}
                      </VStack>
                    </Table.Cell>
                  </Table.Row>
                )}
                </React.Fragment>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* pagination */}
      <Flex justify="space-between" align="center" mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
        <Text fontSize="sm" color="gray.600">
          Showing page {currentPage + 1} of {totalPages}
        </Text>
        <HStack gap={2}>
          <Button 
            size="sm" 
            variant="outline" 
            disabled={currentPage === 0 || isLoading}
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
          >
            <ChevronLeft size={16} />
            Previous
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            disabled={currentPage >= totalPages - 1 || isLoading}
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
          >
            Next
            <ChevronRight size={16} />
          </Button>
        </HStack>
      </Flex>
    </Box>
  );
}